import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export interface TagInput {
  organization_id: string;
  name: string;
  color?: string;
}

export interface Tag extends TagInput {
  id: string;
  created_at: string;
}

const tagsService = {
  async list(organization_id: string, search?: string): Promise<{ data: Tag[] }>{
    validateRequiredParams({ organization_id }, ['organization_id'], {
      service: 'tagsService.list'
    });
    const url = buildApiUrl('/api/crm/tags', { organization_id, search });
    return fetchJSON(url, {
      description: 'List tags',
      context: { organization_id, search }
    });
  },
  async create(payload: TagInput): Promise<{ data: Tag }>{
    validateRequiredParams(payload, ['organization_id', 'name'], {
      service: 'tagsService.create'
    });
    const url = buildApiUrl('/api/crm/tags');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create tag',
      context: { organization_id: payload.organization_id }
    });
  },
  async update(id: string, payload: Partial<TagInput> & { organization_id: string }): Promise<{ data: Tag }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'tagsService.update'
    });
    const url = buildApiUrl(`/api/crm/tags/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update tag',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'tagsService.remove'
    });
    const url = buildApiUrl(`/api/crm/tags/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete tag',
      context: { id, organization_id }
    });
  },
  async getEntityTags(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string
  ): Promise<{ data: Tag[] }>{
    validateRequiredParams({ organizationId, entityType, entityId }, ['organizationId', 'entityType', 'entityId'], {
      service: 'tagsService.getEntityTags'
    });
    const url = buildApiUrl(`/api/crm/${entityType}s/${encodeURIComponent(entityId)}/tags`, { organization_id: organizationId });
    return fetchJSON(url, {
      description: 'Get entity tags',
      context: { organizationId, entityType, entityId }
    });
  },
  async addTagToEntity(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string,
    tagId: string
  ): Promise<{ success: boolean }>{
    validateRequiredParams({ organizationId, entityType, entityId, tagId }, ['organizationId', 'entityType', 'entityId', 'tagId'], {
      service: 'tagsService.addTagToEntity'
    });
    const url = buildApiUrl(`/api/crm/${entityType}s/${encodeURIComponent(entityId)}/tags`);
    return fetchJSON(url, {
      method: 'POST',
      body: { organization_id: organizationId, tag_id: tagId },
      description: 'Add tag to entity',
      context: { organizationId, entityType, entityId, tagId }
    });
  },
  async removeTagFromEntity(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string,
    tagId: string
  ): Promise<{ success: boolean }>{
    validateRequiredParams({ organizationId, entityType, entityId, tagId }, ['organizationId', 'entityType', 'entityId', 'tagId'], {
      service: 'tagsService.removeTagFromEntity'
    });
    const url = buildApiUrl(`/api/crm/${entityType}s/${encodeURIComponent(entityId)}/tags/${encodeURIComponent(tagId)}`, { organization_id: organizationId });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Remove tag from entity',
      context: { organizationId, entityType, entityId, tagId }
    });
  }
};

export default tagsService;
